import { Auth } from "@digatex/digatex-ui-lib";
import axios, { type AxiosError } from "axios";
import { createAuthRefresh } from "axios-auth-refresh";

const baseURL = process.env.REACT_APP_BASE_URL;

const $api = axios.create({
    baseURL,
});

$api.interceptors.request.use(config => {
    if (Auth.isAuthenticated()) {
        config.headers.Authorization = Auth.headers().Authorization;
    }
    return config;
});

const refreshAuthLogic = async (failedRequest: AxiosError) => {
    await Auth.updateToken(() => true);

    const config = failedRequest.response?.config;
    if (!config || !Auth.isAuthenticated()) {
        return Promise.reject(new Error("Token refresh failed"));
    }

    config.headers.Authorization = Auth.headers().Authorization;
    return Promise.resolve();
};

createAuthRefresh($api, refreshAuthLogic, {
    statusCodes: [401],
    deduplicateRefresh: true,
});

const acceptsResponse = (status: number) => status !== 401;

export const rethrowRequestError = (error: unknown): never => {
    if (axios.isCancel(error)) {
        const abortError = new Error("Request aborted");
        abortError.name = "AbortError";
        throw abortError;
    }
    throw error;
};

const buildQuery = (query: string, queryVariables: Record<string, unknown> = {}) => {

    let result = query.replace(/@(\w+)/g, (_, key: string) => {
        const value = queryVariables[key];
        return encodeURIComponent(value == null ? `@${key}` : String(value));
    });

    const usedKeys = [...query.matchAll(/@(\w+)/g)].map(m => m[1]);
    const queryParams = Object.entries(queryVariables)
        .filter(([key]) => !usedKeys.includes(key))
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join("&");

    if (queryParams) result += `?${queryParams}`;

    return result;
};

const executeGetRequest = async (
    query: string,
    abortController?: AbortController
) => {
    try {
        const response = await $api.get(query, {
            signal: abortController?.signal,
            validateStatus: acceptsResponse,
            headers: { "Content-Type": "application/json" },
        });
        return response.data;
    } catch (error) {
        rethrowRequestError(error);
    }
};

const executePostRequest = async (
    query: string,
    variables: Record<string, unknown> | FormData,
    abortController?: AbortController
) => {
    const isFormData = variables instanceof FormData;
    try {
        const response = await $api.post(query, variables, {
            signal: abortController?.signal,
            validateStatus: acceptsResponse,
            headers: isFormData ? {} : { "Content-Type": "application/json" },
        });
        return response.data;
    } catch (error) {
        rethrowRequestError(error);
    }
};

const executeDeleteRequest = async (
    query: string,
    variables: Record<string, unknown>,
    abortController?: AbortController
) => {
    try {
        const response = await $api.delete(query, {
            data: variables,
            signal: abortController?.signal,
            validateStatus: acceptsResponse,
            headers: { "Content-Type": "application/json" },
        });
        return response.data;
    } catch (error) {
        rethrowRequestError(error);
    }
};

const api = {
    executeGetRequest,
    executePostRequest,
    executeDeleteRequest
};

export { $api, buildQuery, api };
